import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  AccordionMultipleProps,
  AccordionSingleProps,
} from "@radix-ui/react-accordion";
import styled, { CSSObject, keyframes } from "styled-components";
import { IconChevronRight } from "@aksara-ui/icons";
import { Box, UnstyledButton, theme } from "@aksara-ui/react";

export const Accordion: React.FC<
  (AccordionSingleProps | AccordionMultipleProps) & { style?: CSSObject }
> = ({ children, style, ...rest }) => {
  return (
    <AccordionPrimitive.Root asChild {...rest}>
      <Box sx={{ ...style }}>{children}</Box>
    </AccordionPrimitive.Root>
  );
};

export const AccordionItem: React.FC<
  AccordionPrimitive.AccordionItemProps & { style?: CSSObject }
> = ({ children, disabled, value, style }) => {
  return (
    <AccordionPrimitive.Item asChild disabled={disabled} value={value}>
      <Box sx={{ ...style }}>{children}</Box>
    </AccordionPrimitive.Item>
  );
};

/** AccordionTrigger child is Button */
export const AccordionTrigger: React.FC<
  AccordionPrimitive.AccordionTriggerProps
> = ({ children, ...rest }) => {
  return (
    <AccordionPrimitive.Trigger asChild {...rest}>
      {children}
    </AccordionPrimitive.Trigger>
  );
};

const AccordionChevron = styled(IconChevronRight)`
  transition: transform 300ms;
  [data-state="open"] & {
    transform: rotate(90deg);
  }
`;

export interface AccordionHeaderProps
  extends AccordionPrimitive.AccordionHeaderProps {
  size?: "md" | "lg";
  isExpand?: boolean;
  isActive?: boolean;
}

/** AccordionHeader child is h3 */
export const AccordionHeader: React.FC<AccordionHeaderProps> = ({
  children,
  size = "md",
  isExpand,
  isActive,
  ...rest
}) => {
  return (
    <AccordionPrimitive.Header asChild {...rest}>
      <AccordionTrigger>
        <Box display={"flex"}>
          <UnstyledButton
            display={"flex"}
            flex={1}
            borderRadius={"lg"}
            justifyContent={isExpand ? "space-between" : "center"}
            alignItems={"center"}
            backgroundColor={
              isActive ? theme.colors.white : theme.colors.white
            }
            _focusVisible={{
              outline: "none",
            }}
          >
            {children}
            {isExpand ? (
              <Box width={16} height={16} paddingRight={30}>
                <AccordionChevron size={16} />
              </Box>
            ) : (
              ""
            )}
          </UnstyledButton>
        </Box>
      </AccordionTrigger>
    </AccordionPrimitive.Header>
  );
};

const slideDown = keyframes`
  from  { height: 0 }
  to { height: var(--radix-accordion-content-height) },`;

const slideUp = keyframes`
  from { height: var(--radix-accordion-content-height) }
  to { height: 0 }`;

const BoxContent = styled(Box)`
  overflow: hidden;
  &[data-state="open"] {
    animation-name: ${slideDown};
    animation-duration: 300ms;
    animation-timing-function: cubic-bezier(0.87, 0, 0.13, 1);
  }
  &[data-state="closed"] {
    animation-name: ${slideUp};
    animation-duration: 300ms;
    animation-timing-function: cubic-bezier(0.87, 0, 0.13, 1);
  }
`;
export const AccordionContent: React.FC<
  AccordionPrimitive.AccordionContentProps
> = ({ children, ...rest }) => {
  return (
    <AccordionPrimitive.Content asChild {...rest}>
      <BoxContent>{children}</BoxContent>
    </AccordionPrimitive.Content>
  );
};
